// IndexedDB utility for storing large image blobs to avoid localStorage quota issues.
// Provides: initImageDB, storeImage, getImage, ensureImageIdFromDataUrl

const DB_NAME = 'orchestratorDB';
const STORE_NAME = 'images';
let dbPromise = null;

function initImageDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const meta = parts[0];
  const base64 = parts[1];
  const mime = meta.match(/data:(.*);base64/)[1];
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = binStr.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function storeImage(dataUrl) {
  const db = await initImageDB();
  const id = 'img_' + Date.now() + '_' + Math.random().toString(36).slice(2);
  const blob = dataUrlToBlob(dataUrl);
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.onerror = () => reject(tx.error);
    const store = tx.objectStore(STORE_NAME);
    store.put({ id, blob });
    tx.oncomplete = () => resolve();
  });
  return id;
}

export async function getImage(id) {
  const db = await initImageDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    tx.onerror = () => reject(tx.error);
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => {
      const record = req.result;
      if (!record) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(record.blob);
    };
    req.onerror = () => reject(req.error);
  });
}

// If layer/overlay has a big dataUrl, move it to IDB and return imageId.
export async function ensureImageIdFromDataUrl(obj) {
  if (obj.imageId) return obj;
  if (obj.dataUrl && obj.dataUrl.length > 2000) {
    try {
      const id = await storeImage(obj.dataUrl);
      return { ...obj, imageId: id, dataUrl: undefined };
    } catch (e) {
      console.warn('Failed storing image in IDB', e);
      return obj;
    }
  }
  return obj;
}

// Bulk migration for a scene
export async function migrateSceneImages(scene) {
  const layers = await Promise.all(scene.layers.map((l) => ensureImageIdFromDataUrl(l)));
  const overlays = [];
  let thumb = scene.thumb;
  let thumbId = scene.thumbId;
  if (!thumbId && thumb && thumb.length > 2000) {
    try {
      thumbId = await storeImage(thumb);
      thumb = undefined;
    } catch (e) {
      console.warn('Failed storing thumbnail');
    }
  }
  return { ...scene, layers, overlays, thumb, thumbId };
}
